function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return typeof key === "symbol" ? key : String(key); }
function _toPrimitive(input, hint) { if (typeof input !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (typeof res !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }
import { useTheme } from '../../system';
import { memoisedGet as get, __DEV__ } from '../../utils';
import * as React from 'react';
import { useLocale } from '../../pp2';
import { useMediaQuery } from '../../hooks';

/**
 * React component to control the visibility of its
 * children based on a couple of queries.
 *
 * The belowQuery, if not empty, will be used 'as is'. The
 * aboveIncludingQuery, if not empty, will be negated. This
 * is because a 'min-width' media query is inclusive, so by
 * passing in a 'max-width' query and then negating it we can
 * end up with an exclusive query.
 *
 * aboveIncluding: matches when the screen size is anything up to this
 * value, excluding it
 * below: matches when the screen size is above this value,
 * including it.
 *
 * if neither aboveIncluding nor above is defined, undefined is returned.
 *
 * e.g. aboveIncluding 1024px, below 1928px
 */
function useMatchSize(aboveIncluding, below) {
  var themeContent = useTheme();
  var {
    aboveIncludingQuery,
    belowQuery
  } = React.useMemo(() => {
    var bpAboveIncluding = getBreakpoint(themeContent, aboveIncluding);
    var bpBelow = getBreakpoint(themeContent, below);
    var aboveIncludingQuery = bpAboveIncluding ? "(min-width: ".concat(bpAboveIncluding, ")") : '';
    var belowQuery = bpBelow ? "(min-width: ".concat(bpBelow, ")") : '';
    return {
      aboveIncludingQuery,
      belowQuery
    };
  }, [themeContent, aboveIncluding, below]);
  var aboveIncludingMatches = useMediaQuery(aboveIncludingQuery);
  var belowMatches = useMediaQuery(belowQuery);
  if (aboveIncludingQuery && belowQuery) {
    return !belowMatches && aboveIncludingMatches;
  } else if (aboveIncludingQuery) {
    return aboveIncludingMatches;
  } else if (belowQuery) {
    return !belowMatches;
  }
  return undefined;
}
function useMatchMediaQuery(query) {
  var matches = useMediaQuery(query || '');
  if (!query) {
    return undefined;
  }
  return matches;
}
function useMatchTheme(theme) {
  var locale = useLocale();
  var isVisible = React.useMemo(() => {
    if (theme === undefined) {
      return undefined;
    }

    // `theme` could be an array of themes or just a single theme.
    // this ensures we have an array in both cases
    var themeArray = [theme].flat();
    var themeIncluded = themeArray.includes(locale) || locale === 'northern-ireland' && themeArray.includes('ni');
    return themeIncluded;
  }, [locale, theme]);
  return isVisible;
}
/**
 * Internal component which combines showing and hiding of components,
 * based on a media query, screen sizes or themes.
 *
 * Each type of comparison is in its own hook for ease of testing and
 * to make the code easier to read.
 */
var ShowHide = _ref => {
  var {
    children,
    theme,
    query,
    above,
    below,
    test,
    hide
  } = _ref;
  var isVisibleFromMediaQuery = useMatchMediaQuery(query);
  var isVisibleFromSize = useMatchSize(above, below);
  var isVisibleFromTheme = useMatchTheme(theme);
  var isVisibleFromTest = typeof test === 'function' ? test() : test;
  // Complicated boolean statement to work out what matches, but ignore the undefined entries:
  // First check if any of the individual values is true (by setting all undefineds to false and
  // ORing the values together)
  // Then check if any of the individual values is false (by settings all undefineds to true and
  // ANDing the values together)
  // If we have at least one true value and no falses, we're good to go!
  var matching = ((isVisibleFromMediaQuery !== null && isVisibleFromMediaQuery !== void 0 ? isVisibleFromMediaQuery : false) || (isVisibleFromSize !== null && isVisibleFromSize !== void 0 ? isVisibleFromSize : false) || (isVisibleFromTheme !== null && isVisibleFromTheme !== void 0 ? isVisibleFromTheme : false) || (isVisibleFromTest !== null && isVisibleFromTest !== void 0 ? isVisibleFromTest : false)) && (isVisibleFromMediaQuery !== null && isVisibleFromMediaQuery !== void 0 ? isVisibleFromMediaQuery : true) && (isVisibleFromSize !== null && isVisibleFromSize !== void 0 ? isVisibleFromSize : true) && (isVisibleFromTheme !== null && isVisibleFromTheme !== void 0 ? isVisibleFromTheme : true) && (isVisibleFromTest !== null && isVisibleFromTest !== void 0 ? isVisibleFromTest : true);
  var isVisible = hide ? !matching : matching;
  return /*#__PURE__*/React.createElement(React.Fragment, null, isVisible ? children : null);
};
/**
 * Hides the child components only if the media query matches and
 * the sizes match and the theme is the current theme.
 *
 * See {@link Show} for more details.
 */
export var Hide = props => {
  return ShowHide(_objectSpread(_objectSpread({}, props), {}, {
    hide: true
  }));
};
if (__DEV__) {
  Hide.displayName = 'Hide';
}

/**
 * Shows the child components only if the media query matches and the
 * sizes match and the theme is the current theme.
 *
 * `theme` can be a single theme or an array of themes.
 *
 * `above` is inclusive, while `below` is exclusive. This is to prevent
 * there being a single pixel width when nothing is matched.
 *
 * `above` and `below` can either be the name of a breakpoint (e.g. `sm`,
 * `lg`, etc) or a literal value such as `16px` or `5rem`.
 *
 * `query` is a media query which must match. (e.g. `(prefers-color-scheme: dark))
 *
 * `test` is either a boolean value, or a function which returns a boolean
 * value.
 *
 * All items which are specified must match for the child components to
 * be shown.
 *
 * Also see {@link Hide} for a version of this component which hides
 * children instead.
 */
export var Show = props => {
  return ShowHide(_objectSpread(_objectSpread({}, props), {}, {
    hide: false
  }));
};
if (__DEV__) {
  Show.displayName = 'Show';
}
var getBreakpoint = (theme, value) => get(theme, "breakpoints.".concat(value), value);
/**
 * Creates a media query based on the breakpoint, below or above values.
 *
 * If `below` is specified, it will be used. Otherwise, if `above` is
 * specified, it will be used. If neither of these are specified then
 * `breakpoint` itself is returned.
 *
 * `below` and `above` can either be a breakpoint name (e.g. `lg`), or a
 * literal value (e.g. '64px')
 *
 * @deprecated
 */
export function useQuery(_ref2) {
  var {
    breakpoint = '',
    below,
    above
  } = _ref2;
  var theme = useTheme();
  var bpBelow = getBreakpoint(theme, below);
  var bpAbove = getBreakpoint(theme, above);
  var query = breakpoint;
  if (bpBelow) {
    query = "(max-width: ".concat(bpBelow, ")");
  } else if (bpAbove) {
    query = "(min-width: ".concat(bpAbove, ")");
  }
  return query;
}

// these exports are only for use in tests
export var testExport = {
  useMatchSize,
  useMatchTheme,
  useMatchMediaQuery
};